import type { LicenseFingerprint, LicenseView } from "../api/licenseService";

/**
 * 本实例是否启用 license:后端 GET /platform/license 的 status.mode 不是 DISABLED。
 * 拉不到(403 / 网络 / 老后端 404)一律按「未启用」—— SaaS 上宁可藏入口,只能直链进。
 */
export function isLicenseEnabled(view: LicenseView | null | undefined): boolean {
  const mode = view?.status?.mode;
  return !!mode && mode !== "DISABLED";
}

/** 指纹里三项哈希(不含 label)的键,顺序即展示顺序。 */
export const FINGERPRINT_KEYS = ["machine_id", "product_uuid", "mac"] as const;
export type FingerprintKey = (typeof FINGERPRINT_KEYS)[number];

const SHA256_HEX = /^[0-9a-f]{64}$/i;

export function isSha256Hex(v: string | null | undefined): boolean {
  return !!v && SHA256_HEX.test(v.trim());
}

/** 复制给签发方的指纹 JSON(只带三项哈希;空项省略)。和签发表单的「粘贴指纹 JSON」互为逆。 */
export function fingerprintToJson(fp: LicenseFingerprint | null | undefined): string {
  const out: Record<string, string> = {};
  for (const k of FINGERPRINT_KEYS) {
    const v = fp?.[k];
    if (v && v.trim()) out[k] = v.trim();
  }
  return JSON.stringify(out);
}

/**
 * 解析粘贴进来的指纹 JSON。认 snake_case(后端原样)与 camelCase(machineId…)两种键;
 * 一项都没解出来 → null。
 */
export function parseFingerprintJson(text: string): LicenseFingerprint | null {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    return null;
  }
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const pick = (snake: string, camel: string) => {
    const v = o[snake] ?? o[camel];
    return typeof v === "string" && v.trim() ? v.trim() : undefined;
  };
  const fp: LicenseFingerprint = {
    machine_id: pick("machine_id", "machineId"),
    product_uuid: pick("product_uuid", "productUuid"),
    mac: pick("mac", "mac"),
  };
  const label = pick("label", "label");
  if (label) fp.label = label;
  return FINGERPRINT_KEYS.some((k) => fp[k]) ? fp : null;
}

/** license 文件的最小形状校验(format / payload / signature 在)。只做前端预检,验签以后端为准。 */
export function looksLikeLicenseFile(text: string): boolean {
  try {
    const o = JSON.parse(text) as Record<string, unknown>;
    return (
      !!o &&
      typeof o === "object" &&
      typeof o.payload === "string" &&
      typeof o.signature === "string"
    );
  } catch {
    return false;
  }
}

/** ISO 时间 → 「YYYY-MM-DD HH:mm」本地时间;空 → "—"。 */
export function formatInstant(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(
    d.getMinutes()
  )}`;
}
